import React from "react";
import "./PrivacyPolicy.scss";

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      <div className="privacy-policy__container">
        <h1>Privacy Policy</h1>
        <div className="privacy-policy__content">
          <section className="intro">
            <h2>Introduction</h2>
            <p>
              At ShadowDial, we are committed to protecting your privacy and
              ensuring transparency about our data practices. This Privacy
              Policy explains how we handle information when you use our fake
              call application ("ShadowDial" or "the App").
            </p>
            <p>
              <strong>Our Privacy Promise:</strong> ShadowDial is designed with
              privacy at its core. We do not collect, store, transmit, or access
              any personal, confidential, or sensitive information from your
              device or usage of the app.
            </p>
          </section>

          <section>
            <h2>Information We Do NOT Collect</h2>
            <p>
              ShadowDial operates entirely on your device without requiring any
              data collection. Specifically, we do NOT collect:
            </p>
            <ul>
              <li>
                <strong>Personal Information:</strong> Names, email addresses,
                phone numbers, or contact details
              </li>
              <li>
                <strong>Device Information:</strong> Device identifiers,
                hardware specifications, or system information
              </li>
              <li>
                <strong>Usage Data:</strong> App usage patterns, frequency of
                use, or feature interactions
              </li>
              <li>
                <strong>Location Data:</strong> GPS coordinates, location
                history, or geographic information
              </li>
              <li>
                <strong>Contact Lists:</strong> Your phone contacts, call logs,
                or communication history
              </li>
              <li>
                <strong>Media Files:</strong> Photos, videos, audio files, or
                other media from your device
              </li>
              <li>
                <strong>Authentication Data:</strong> Passwords, biometric data,
                or security credentials
              </li>
              <li>
                <strong>Financial Information:</strong> Payment details,
                transaction history, or financial data
              </li>
              <li>
                <strong>Social Media Data:</strong> Social network profiles,
                posts, or connections
              </li>
              <li>
                <strong>Behavioral Data:</strong> User preferences, habits, or
                predictive analytics
              </li>
            </ul>
          </section>

          <section>
            <h2>How ShadowDial Works Privately</h2>
            <p>
              ShadowDial functions entirely offline and locally on your device:
            </p>
            <ul>
              <li>
                <strong>Local Storage Only:</strong> All app settings, custom
                caller names, and configurations are stored locally on your
                device
              </li>
              <li>
                <strong>No Internet Connection Required:</strong> The app
                functions completely offline without requiring network access
              </li>
              <li>
                <strong>No Data Transmission:</strong> No information is sent to
                our servers, third parties, or external services
              </li>
              <li>
                <strong>Device-Only Operation:</strong> All fake call
                functionality is generated and managed locally
              </li>
            </ul>
          </section>

          <section>
            <h2>App Permissions</h2>
            <p>
              ShadowDial may request the following device permissions solely for
              app functionality:
            </p>
            <ul>
              <li>
                <strong>Phone Permissions:</strong> To simulate incoming calls
                and provide realistic call interfaces
              </li>
              <li>
                <strong>Audio Permissions:</strong> To play ringtones and call
                sounds during fake calls
              </li>
              <li>
                <strong>System Alert Window:</strong> To display fake call
                screens over other applications
              </li>
              <li>
                <strong>Proximity Sensor:</strong> To enable screen behavior
                similar to real calls
              </li>
              <li>
                <strong>Notification Access:</strong> To schedule and trigger
                fake calls as configured
              </li>
            </ul>
            <p>
              <strong>Important:</strong> These permissions are used exclusively
              for app functionality and do not enable any data collection or
              transmission.
            </p>
          </section>

          <section>
            <h2>Third-Party Services</h2>
            <p>
              ShadowDial does not integrate with, connect to, or share data with
              any third-party services, analytics platforms, advertising
              networks, or external APIs. The app operates in complete isolation
              from external services.
            </p>
          </section>

          <section>
            <h2>Data Security</h2>
            <p>
              Since ShadowDial does not collect or transmit any data, there are
              no data security risks associated with our app. All information
              remains securely on your device under your control. We recommend:
            </p>
            <ul>
              <li>
                Keeping your device updated with the latest security patches
              </li>
              <li>Using device-level security features like screen locks</li>
              <li>Being cautious when granting app permissions</li>
            </ul>
          </section>

          <section>
            <h2>Children's Privacy</h2>
            <p>
              ShadowDial does not collect any information from users of any age,
              including children under 13. Since no data collection occurs,
              there are no special provisions required for children's privacy
              protection.
            </p>
          </section>

          <section>
            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or for legal reasons. Any changes will be
              posted within the app and on our website. Continued use of
              ShadowDial after changes constitutes acceptance of the updated
              policy.
            </p>
          </section>

          <section>
            <h2>Your Rights and Choices</h2>
            <p>
              Since ShadowDial does not collect any personal data, traditional
              data rights (such as access, deletion, or portability) are not
              applicable. However, you maintain complete control over:
            </p>
            <ul>
              <li>All app settings and configurations stored on your device</li>
              <li>App permissions granted through your device settings</li>
              <li>Uninstalling the app at any time to remove all local data</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
